import { StockWithVariant } from '@/api/daily-stock-api';
import { Button } from '@/components/ui/button';
import { Table, TableBody } from '@/components/ui/table';
import { AdjustInStockDialog } from '@/pages/stock/components/AdjustInStockDialog';
import { AdjustOutStockDialog } from '@/pages/stock/components/AdjustOutStockDialog';
import { Minus, Plus, TrendingDown, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { StockTableEmpty } from './StockTableEmpty';
import { StockTableHeader } from './StockTableHeader';
import { StockTableLoading } from './StockTableLoading';
import { StockTableRow } from './StockTableRow';
import { Skeleton } from '@/components/ui/skeleton';

interface StockTableProps {
  stocks: StockWithVariant[];
  isLoading?: boolean;
  onAdjustOut?: (stock: StockWithVariant) => void;
  onAdjustIn?: (stock: StockWithVariant) => void;
  onStockUpdate?: () => void;
}

// Shared hook for dialog management
function useStockDialogs(onStockUpdate?: () => void) {
  const [selectedStock, setSelectedStock] = useState<StockWithVariant | null>(null);
  const [isAdjustInOpen, setIsAdjustInOpen] = useState(false);
  const [isAdjustOutOpen, setIsAdjustOutOpen] = useState(false);

  const handleAdjustIn = (stock: StockWithVariant) => {
    setSelectedStock(stock);
    setIsAdjustInOpen(true);
  };

  const handleAdjustOut = (stock: StockWithVariant) => {
    setSelectedStock(stock);
    setIsAdjustOutOpen(true);
  };

  const dialogs = selectedStock ? (
    <>
      <AdjustInStockDialog
        isOpen={isAdjustInOpen}
        onClose={() => setIsAdjustInOpen(false)}
        stock={selectedStock}
        sku={selectedStock.sku}
        onSuccess={onStockUpdate}
      />
      <AdjustOutStockDialog
        isOpen={isAdjustOutOpen}
        onClose={() => setIsAdjustOutOpen(false)}
        stock={selectedStock}
        sku={selectedStock.sku}
        onSuccess={onStockUpdate}
      />
    </>
  ) : null;

  return { handleAdjustIn, handleAdjustOut, dialogs };
}

export function StockTable({
  stocks,
  isLoading = false,
  onAdjustOut,
  onAdjustIn,
  onStockUpdate,
}: StockTableProps) {
  const { handleAdjustIn, handleAdjustOut, dialogs } = useStockDialogs(onStockUpdate);

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm w-full max-w-full overflow-x-auto">
      <div className="min-w-full">
        <Table className="w-full table-auto">
          <StockTableHeader />
          <TableBody>
            {stocks.map((stock, index) => (
              <StockTableRow
                key={stock.id}
                stock={stock}
                onAdjustOut={onAdjustOut || handleAdjustOut}
                onAdjustIn={onAdjustIn || handleAdjustIn}
                index={index}
              />
            ))}
            {isLoading && <StockTableLoading />}
          </TableBody>
        </Table>
        {stocks.length === 0 && !isLoading && <StockTableEmpty />}
      </div>
      {dialogs}
    </div>
  );
}

export function StockTableMobile({
  stocks,
  isLoading = false,
  onAdjustIn,
  onAdjustOut,
  onStockUpdate,
}: StockTableProps) {
  const { handleAdjustIn, handleAdjustOut, dialogs } = useStockDialogs(onStockUpdate);

  return (
    <div className="space-y-2 md:space-y-2.5">
      {stocks.map((stock) => (
        <StockTableMobileCard
          key={stock.id}
          stock={stock}
          onAdjustIn={onAdjustIn || handleAdjustIn}
          onAdjustOut={onAdjustOut || handleAdjustOut}
        />
      ))}
      
      {isLoading && (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 space-y-3">
              <div className="flex items-start space-x-3">
                <Skeleton className="h-12 w-12 rounded-lg shrink-0" />
                <div className="flex-1 space-y-2 min-w-0">
                  <div className="flex justify-between items-center">
                    <Skeleton className="h-4 w-32 rounded" />
                    <Skeleton className="h-4 w-12 rounded" />
                  </div>
                  <Skeleton className="h-3 w-20 rounded" />
                </div>
              </div>
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <div className="flex justify-between"><Skeleton className="h-3.5 w-24 rounded" /><Skeleton className="h-3.5 w-10 rounded" /></div>
                <div className="flex justify-between"><Skeleton className="h-3.5 w-16 rounded" /><Skeleton className="h-3.5 w-8 rounded" /></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {stocks.length === 0 && !isLoading && <StockTableEmpty />}
      {dialogs}
    </div>
  );
}

// Utility functions
const getStockStats = (stock: StockWithVariant) => {
  const totalStock = stock.stockInHand ?? 0;
  const totalSold = stock.soldQty ?? 0;
  const totalReceived = stock.receiveQty ?? 0;
  const totalInflows = totalReceived + (stock.adjustInQty ?? 0);
  const totalOutflows = totalSold + (stock.adjustOutQty ?? 0);
  const netMovement = totalInflows - totalOutflows;

  let stockStatus: 'In Stock' | 'Low Stock' | 'Out of Stock' = 'In Stock';
  if (totalStock === 0) {
    stockStatus = 'Out of Stock';
  } else if (totalStock < 10) {
    stockStatus = 'Low Stock';
  }

  return {
    totalStock,
    totalSold,
    totalReceived,
    stockStatus,
    totalInflows,
    totalOutflows,
    netMovement,
  };
};

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(price);
};

function StockTableMobileCard({
  stock,
  onAdjustIn,
  onAdjustOut,
}: {
  stock: StockWithVariant;
  onAdjustIn?: (stock: StockWithVariant) => void;
  onAdjustOut?: (stock: StockWithVariant) => void;
}) {
  const stats = getStockStats(stock);
  const { variantName, imageUrl, price, sku, isActive = true } = stock;

  const getStatusClass = () => {
    if (stats.stockStatus === 'Out of Stock') return 'bg-red-100 text-red-800';
    if (stats.stockStatus === 'Low Stock') return 'bg-orange-100 text-orange-800';
    return 'bg-green-100 text-green-800';
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-3">
      <div className="flex items-start space-x-3">
        {/* Product Image */}
        <div className="h-12 w-12 md:h-14 md:w-14 rounded-lg ring-2 ring-slate-100 overflow-hidden shrink-0">
          <img
            src={imageUrl ?? ''}
            alt={variantName}
            className="h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <div className="h-full w-full bg-gradient-to-br from-blue-50 to-indigo-100 text-slate-600 font-semibold flex items-center justify-center text-sm">
            {variantName.substring(0, 2).toUpperCase()}
          </div>
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm md:text-base text-slate-900 truncate">
                {variantName}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">SKU: {sku}</p>
            </div>
            <span
              className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${
                isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>

          {/* Stock Movement Info */}
          <div className="space-y-2 mb-2">
            {/* Stock in Hand */}
            <div className="flex items-center justify-between space-x-1 md:space-x-2 min-w-0">
              <div className="flex items-center space-x-1 md:space-x-1.5 min-w-0 flex-shrink">
                <div
                  className={`w-2 h-2 rounded-full shrink-0 ${
                    stats.totalStock > 0
                      ? 'bg-blue-500'
                      : stats.totalStock === 0
                      ? 'bg-orange-500'
                      : 'bg-red-500'
                  }`}
                ></div>
                <span
                  className={`text-xs font-medium truncate ${
                    stats.totalStock > 0
                      ? 'text-blue-700'
                      : stats.totalStock === 0
                      ? 'text-orange-700'
                      : 'text-red-700'
                  }`}
                >
                  Stock in Hand
                </span>
              </div>
              <div className="flex items-center space-x-1 md:space-x-1.5 flex-shrink-0">
                <span className="text-lg font-bold text-slate-900 whitespace-nowrap">
                  {stats.totalStock.toLocaleString()}
                </span>
                {stock.receiveQty > 0 && (
                  <span className="inline-flex items-center px-1 md:px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-green-100 text-green-700 whitespace-nowrap">
                    +{stock.receiveQty.toLocaleString()}
                  </span>
                )}
                {stock.adjustInQty > 0 && (
                  <span className="inline-flex items-center px-1 md:px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-green-100 text-green-700 whitespace-nowrap">
                    +{stock.adjustInQty.toLocaleString()}
                  </span>
                )}
                {stock.adjustOutQty > 0 && (
                  <span className="inline-flex items-center px-1 md:px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-red-100 text-red-700 whitespace-nowrap">
                    -{stock.adjustOutQty.toLocaleString()}
                  </span>
                )}
              </div>
            </div>

            {/* Sold */}
            <div className="flex items-center justify-between space-x-2">
              <div className="flex items-center space-x-1.5">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-xs text-green-700 font-medium">Sold</span>
              </div>
              <span className="text-xs md:text-sm font-bold text-slate-900 whitespace-nowrap flex-shrink-0">
                {stats.totalSold.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Price & Status */}
          <div className="pt-2 border-t border-slate-200 flex items-center justify-between mb-2">
            <div>
              <div className="text-[10px] text-slate-500 mb-0.5">Price</div>
              <div className="font-semibold text-green-700 text-xs md:text-sm">
                {price ? formatPrice(price) : 'N/A'}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-slate-500 mb-0.5">Status</div>
              <span className={`text-[10px] md:text-xs font-medium px-2 py-0.5 rounded-full ${getStatusClass()}`}>
                {stats.stockStatus}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          {(onAdjustIn || onAdjustOut) && (
            <div className="pt-2 border-t border-slate-200 flex gap-1.5">
              {onAdjustIn && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onAdjustIn(stock)}
                  className="flex-1 h-8 text-xs hover:bg-green-50 hover:border-green-300"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Adjust In
                </Button>
              )}
              {onAdjustOut && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onAdjustOut(stock)}
                  className="flex-1 h-8 text-xs hover:bg-red-50 hover:border-red-300"
                >
                  <Minus className="h-3 w-3 mr-1" />
                  Adjust Out
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
